import type { FC, ReactNode } from 'react'
import type { GetStaticProps } from 'next'
import type { Testimonial } from '@/types'

import Link from 'next/link'
import { ArrowRight, Building2, Calculator, Rocket, TrendingUp } from 'lucide-react'
import { cn } from '@/lib/utils'
import { getTestimonials, safely } from '@/lib/content'
import { Seo } from '@/components/generals/seo'
import { Layout } from '@/components/generals/layout'
import { ButtonLink } from '@/components/generals/button'
import { PageHero } from '@/components/sections/page-hero'
import { SplitFeature } from '@/components/sections/split-feature'
import { Testimonials } from '@/components/landing/testimonials'
import { BudgetPreview } from '@/components/features/budget-preview'

import {
    CloseChecklist,
    EntityConsolidation,
    StartupRunway
} from '@/components/solutions/solution-visuals'

const AUDIENCES = [
    { id: 'startups', label: 'Startups', body: 'Investor-grade numbers without a finance team.', Icon: Rocket },
    { id: 'growing-businesses', label: 'Growing businesses', body: 'Spend control that scales with headcount.', Icon: TrendingUp },
    { id: 'finance-teams', label: 'Finance teams', body: 'A faster close and more time for analysis.', Icon: Calculator },
    { id: 'enterprise', label: 'Enterprise', body: 'One view across entities, currencies and teams.', Icon: Building2 }
]

interface RecommendedPlanProps {
    plan: string
    dark?: boolean
}

const RecommendedPlan: FC<RecommendedPlanProps> = ({ plan, dark = false }): ReactNode => {
    return (
        <div className={cn('mt-9 flex flex-wrap items-center gap-3 border-t pt-6 text-sm', dark ? 'border-cream/10' : 'border-burgundy/10')}>
            <span className={cn('font-mono text-[10.5px] uppercase tracking-[0.18em]', dark ? 'text-beige' : 'text-stone')}>
                Recommended plan
            </span>

            <Link
                href='/pricing'
                className={cn('group inline-flex items-center gap-1.5 font-medium', dark ? 'text-cream' : 'text-burgundy')}
            >
                {plan}
                <ArrowRight aria-hidden='true' className='size-3.5 transition-transform duration-300 group-hover:translate-x-0.5' />
            </Link>
        </div>
    )
}

interface SolutionsProps {
    testimonials: Array<Testimonial>
}

const Solutions: FC<SolutionsProps> = ({ testimonials }): ReactNode => {
    return (
        <Layout>
            <Seo
                title='Solutions'
                description='Veyra for startups, growing businesses, finance teams and enterprise companies. See how AI financial management fits your stage and your team.'
            />

            <PageHero
                eyebrow='Solutions'
                title={<>Built for every stage of <span className='text-stone'>financial complexity.</span></>}
                description='Whether you are a founder doing the books at night or a finance organization running twelve entities, Veyra adapts to how your company works today and grows with it.'
                actions={(
                    <>
                        <ButtonLink href='/demo' size='lg' arrow>Talk to our team</ButtonLink>
                        <ButtonLink href='/pricing' variant='secondary' size='lg'>See pricing</ButtonLink>
                    </>
                )}
            >
                <ul className='grid border-l border-t border-burgundy/10 sm:grid-cols-2 lg:grid-cols-4'>
                    {AUDIENCES.map(({ id, label, body, Icon }) => (
                        <li key={id} className='border-b border-r border-burgundy/10'>
                            <a href={`#${id}`} className='group flex h-full flex-col p-6 transition-colors duration-300 hover:bg-ivory/70'>
                                <span className='flex items-center justify-between'>
                                    <span className='flex size-10 items-center justify-center rounded-full bg-burgundy text-gold'>
                                        <Icon aria-hidden='true' className='size-4' />
                                    </span>
                                    <ArrowRight aria-hidden='true' className='size-4 text-stone transition-transform duration-300 group-hover:translate-x-0.5 group-hover:text-burgundy' />
                                </span>
                                <span className='mt-6 font-medium text-burgundy'>{label}</span>
                                <span className='mt-1.5 text-sm text-stone-dark'>{body}</span>
                            </a>
                        </li>
                    ))}
                </ul>
            </PageHero>

            <SplitFeature
                id='startups'
                label='For startups'
                title='Know your runway before your investors ask.'
                description='Founders without a finance team get investor-grade numbers from day one. Veyra connects your bank and cards, categorizes every expense and keeps runway, burn and cash forecasts current on its own.'
                points={[
                    'Runway, burn and zero-cash date updated daily',
                    'Investor updates drafted straight from your books',
                    'Clean, audit-ready records long before your first audit'
                ]}
                visual={<StartupRunway />}
                tone='cream'
                aside={<RecommendedPlan plan='Starter' />}
            />

            <SplitFeature
                id='growing-businesses'
                label='For growing businesses'
                title='Scale spend without losing control.'
                description='As teams multiply, so do cards, vendors and surprises. Give every department a live budget and an owner, approve what matters and let routine spend flow, with finance seeing everything in real time.'
                points={[
                    'Budgets by team, project and vendor, tracked live',
                    'Alerts when spend is trending over, not after',
                    'Approval rules by amount and category'
                ]}
                visual={<BudgetPreview />}
                reverse
                tone='burgundy'
                aside={<RecommendedPlan plan='Growth' dark />}
            />

            <SplitFeature
                id='finance-teams'
                label='For finance teams'
                title='Close faster and spend your time on analysis.'
                description='Reconciliations happen throughout the month, accruals follow templates and the flux analysis drafts itself. Your team starts each close with most of the work already done and finishes in days, not weeks.'
                points={[
                    'Continuous reconciliation of bank and card activity',
                    'Close checklists with owners, due dates and status',
                    'AI-drafted variance commentary for review'
                ]}
                visual={<CloseChecklist />}
                tone='cream'
                aside={<RecommendedPlan plan='Growth' />}
            />

            <SplitFeature
                id='enterprise'
                label='For enterprise'
                title='One view across every entity and currency.'
                description='Consolidate subsidiaries, eliminate intercompany activity and report in any currency, with the controls your auditors expect: single sign-on, granular permissions, approval chains and a complete audit trail.'
                points={[
                    'Multi-entity and multi-currency consolidation',
                    'SSO, SAML and role-based permissions',
                    'Dedicated success manager and custom integrations'
                ]}
                visual={<EntityConsolidation />}
                reverse
                tone='paper'
                aside={<RecommendedPlan plan='Enterprise' />}
            />

            <Testimonials testimonials={testimonials} />
        </Layout>
    )
}

export const getStaticProps: GetStaticProps<SolutionsProps> = async () => {
    const testimonials = await safely(getTestimonials, [], 'testimonials')

    return {
        props: { testimonials },
        revalidate: 60
    }
}

export default Solutions
